import React, { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { createCostServiceSchema } from "../../schemavalidations/costserviceschema";
import {
  CreateCostService,
  CostService,
  Zone,
  Band,
} from "../../types/costService";
import { FormField, Input, Select, Textarea } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import Button from "../UI/Button";
import { Plus, X, Wrench, Grid3x3, Pencil } from "lucide-react";
import { PartnerSearchDropdown } from "../services/PartnerSearchDropdown";

interface Partner {
  _id: string;
  companyName: string;
  isDeleted?: boolean;
}

interface CostServiceFormProps {
  service?: CostService;
  onSubmit: (data: CreateCostService | Partial<CostService>) => Promise<void>;
  loading: boolean;
  isEditMode?: boolean;
  onTypeChange?: (type: string) => void;
  /** Pass your redux dispatch / axios call here so the form can search partners */
  fetchPartners?: (params: {
    search: string;
    page: number;
    limit: number;
  }) => Promise<{ partners: Partner[]; pagination: { pages: number } }>;
}

interface FormValues {
  name: string;
  description?: string | null;
  type: string;
  price?: string | null;
  tax: string;
  partnerId?: string | null;
}

interface FormZone {
  name: string;
  postalFrom: string;
  postalTo: string;
}

interface FormBand {
  label: string;
  minTn: number | string;
  maxTn: number | string;
  ldm: number | string;
}

interface PriceMatrixIndexCell {
  zoneIndex: number;
  bandIndex: number;
  price: number;
}

const PREFILLED_ZONES: FormZone[] = [
  { name: "Tallinn", postalFrom: "10001", postalTo: "13999" },
  { name: "Harjumaa", postalFrom: "74001", postalTo: "76999" },
  { name: "Lääne-Harju", postalFrom: "76001", postalTo: "76999" },
  { name: "Ida-Viru", postalFrom: "20001", postalTo: "44999" },
  { name: "Lääne-Viru", postalFrom: "44001", postalTo: "49999" },
  { name: "Pärnumaa", postalFrom: "80001", postalTo: "89999" },
  { name: "Tartumaa", postalFrom: "50001", postalTo: "62999" },
  { name: "Viljandimaa", postalFrom: "69001", postalTo: "72999" },
  { name: "Võrumaa", postalFrom: "64001", postalTo: "66999" },
  { name: "Valgamaa", postalFrom: "66001", postalTo: "68999" },
  { name: "Põlvamaa", postalFrom: "63001", postalTo: "64999" },
  { name: "Jõgevamaa", postalFrom: "48001", postalTo: "49999" },
  { name: "Järvamaa", postalFrom: "72001", postalTo: "73999" },
  { name: "Raplamaa", postalFrom: "78001", postalTo: "79999" },
  { name: "Läänemaa", postalFrom: "90001", postalTo: "92999" },
  { name: "Saaremaa", postalFrom: "93001", postalTo: "94999" },
];

// ---------------------------------------------------------------------------
// ZoneCombobox
// ---------------------------------------------------------------------------
interface ZoneComboboxProps {
  value: string;
  onChange: (name: string) => void;
  onSelect: (zone: FormZone) => void;
  placeholder: string;
}

const ZoneCombobox: React.FC<ZoneComboboxProps> = ({
  value,
  onChange,
  onSelect,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = value.trim()
    ? PREFILLED_ZONES.filter((z) =>
        z.name.toLowerCase().includes(value.toLowerCase()),
      )
    : PREFILLED_ZONES;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
          {filtered.map((zone) => (
            <li
              key={zone.name}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(zone);
                setOpen(false);
              }}
              className="flex items-center justify-between px-3 py-2 hover:bg-blue-50 cursor-pointer select-none"
            >
              <span className="font-medium text-gray-800">{zone.name}</span>
              <span className="text-xs text-gray-400 ml-3 shrink-0 tabular-nums">
                {zone.postalFrom} – {zone.postalTo}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PriceTable
// ---------------------------------------------------------------------------
interface PriceTableProps {
  zones: FormZone[];
  bands: FormBand[];
  prices: Record<string, string>;
  onChange: (key: string, value: string) => void;
  readonly?: boolean;
  weightLdmLabel: string;
  zonePrefix: string;
  bandPrefix: string;
  allCellsNote: string;
}

const PriceTable: React.FC<PriceTableProps> = ({
  zones,
  bands,
  prices,
  onChange,
  readonly = false,
  weightLdmLabel,
  zonePrefix,
  allCellsNote,
}) => {
  if (zones.length === 0 || bands.length === 0) return null;

  return (
    <div className="w-full overflow-x-auto mt-4">
      <table className="w-full border-collapse border border-gray-300 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th
              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300 bg-gray-100 whitespace-nowrap"
              style={{ width: "180px", minWidth: "180px" }}
            >
              {weightLdmLabel}
            </th>
            {zones.map((zone, zIdx) => (
              <th
                key={zIdx}
                className="px-3 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300 whitespace-nowrap"
              >
                {zone.name || `${zonePrefix} ${zIdx + 1}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bands.map((band, bIdx) => (
            <tr key={bIdx} className="hover:bg-gray-50">
              <td
                className="px-3 py-2 text-xs font-medium text-gray-700 border border-gray-300 bg-gray-50 whitespace-nowrap"
                style={{ width: "180px", minWidth: "180px" }}
              >
                {band.label || `Band ${bIdx + 1}`}
              </td>
              {zones.map((_, zIdx) => {
                const key = `${zIdx}-${bIdx}`;
                const val = prices[key] ?? "";
                const isEmpty = val === "" || val === "0";
                return (
                  <td key={zIdx} className="border border-gray-300 p-0">
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={val}
                      readOnly={readonly}
                      onChange={(e) => onChange(key, e.target.value)}
                      placeholder="0"
                      className={`w-full px-2 py-2 text-sm text-center border-0 outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset transition-colors ${
                        isEmpty && !readonly
                          ? "bg-red-50 text-red-600"
                          : "bg-white"
                      } ${readonly ? "cursor-not-allowed bg-gray-50" : ""}`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!readonly && (
        <p className="text-xs text-gray-500 mt-1">{allCellsNote}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CostServiceForm
// ---------------------------------------------------------------------------
export const CostServiceForm: React.FC<CostServiceFormProps> = ({
  service,
  onSubmit,
  loading,
  isEditMode = false,
  onTypeChange,
  fetchPartners,
}) => {
  const { t } = useTranslation(["costservices", "common"]);
  const [readonly, setReadonly] = useState(isEditMode);
  const [zones, setZones] = useState<FormZone[]>([]);
  const [bands, setBands] = useState<FormBand[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [matrixErrors, setMatrixErrors] = useState<string[]>([]);

  // Track selected partnerId separately so PartnerSearchDropdown controls it
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    null,
  );
  // The partner object for display purposes when editing
  const [initialPartner, setInitialPartner] = useState<Partner | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(createCostServiceSchema) as any,
    defaultValues: {
      name: "",
      description: "",
      type: "HOUR",
      price: "",
      tax: "NO_TAX",
      partnerId: null,
    },
  });

  const watchedType = watch("type");
  const isMatrix = watchedType === "MATRIX";

  // Keep react-hook-form in sync when the combobox selection changes
  const handlePartnerChange = (id: string | null) => {
    setSelectedPartnerId(id);
    setValue("partnerId", id);
  };

  useEffect(() => {
    if (service && isEditMode) {
      reset({
        name: service.name,
        description: service.description || "",
        type: service.type,
        price: service.price != null ? String(service.price) : "",
        tax: service.tax || "NO_TAX",
        partnerId: service.partnerId || null,
      });

      const pid = service.partnerId || null;
      setSelectedPartnerId(pid);
      if (service.partnerInfo) {
        setInitialPartner({
          _id: pid as string,
          companyName: service.partnerInfo.name,
          isDeleted: service.partnerInfo.isDeleted,
        });
      }

      if (service.type === "MATRIX") {
        setZones(
          (service.zones || []).map((z) => ({
            name: z.name,
            postalFrom: z.postalFrom,
            postalTo: z.postalTo,
          })),
        );
        setBands(
          (service.bands || []).map((b) => ({
            label: b.label,
            minTn: b.minTn,
            maxTn: b.maxTn,
            ldm: b.ldm,
          })),
        );

        const priceMap: Record<string, string> = {};
        service.priceMatrix?.forEach((cell) => {
          const zIdx = service.zones!.findIndex((z) => z._id === cell.zoneId);
          const bIdx = service.bands!.findIndex((b) => b._id === cell.bandId);
          if (zIdx !== -1 && bIdx !== -1)
            priceMap[`${zIdx}-${bIdx}`] = String(cell.price);
        });
        setPrices(priceMap);
      } else {
        setZones([]);
        setBands([]);
        setPrices({});
      }
    } else if (!isEditMode) {
      reset({
        name: "",
        description: "",
        type: "HOUR",
        price: "",
        tax: "NO_TAX",
        partnerId: null,
      });
      setSelectedPartnerId(null);
      setInitialPartner(null);
      setZones([]);
      setBands([]);
      setPrices({});
    }
  }, [service, isEditMode, reset]);

  // Zone helpers
  const addZone = () =>
    setZones((prev) => [...prev, { name: "", postalFrom: "", postalTo: "" }]);

  const removeZone = (idx: number) => {
    setZones((prev) => prev.filter((_, i) => i !== idx));
    setPrices((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([key, val]) => {
        const [z, b] = key.split("-").map(Number);
        if (z < idx) next[key] = val;
        else if (z > idx) next[`${z - 1}-${b}`] = val;
      });
      return next;
    });
  };

  const updateZone = (idx: number, field: keyof FormZone, value: string) =>
    setZones((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });

  const applyZonePreset = (idx: number, preset: FormZone) =>
    setZones((prev) => {
      const next = [...prev];
      next[idx] = { ...preset };
      return next;
    });

  const prefillAllCounties = () => setZones([...PREFILLED_ZONES]);

  // Band helpers
  const addBand = (count: number = 1) => {
    const newBands: FormBand[] = Array.from({ length: count }, () => ({
      label: "",
      minTn: "",
      maxTn: "",
      ldm: "",
    }));
    setBands((prev) => [...prev, ...newBands]);
  };

  const removeBand = (idx: number) => {
    setBands((prev) => prev.filter((_, i) => i !== idx));
    setPrices((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([key, val]) => {
        const [z, b] = key.split("-").map(Number);
        if (b < idx) next[key] = val;
        else if (b > idx) next[`${z}-${b - 1}`] = val;
      });
      return next;
    });
  };

  const updateBand = (idx: number, field: keyof FormBand, value: string) => {
    setBands((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handlePriceChange = (key: string, value: string) =>
    setPrices((prev) => ({ ...prev, [key]: value }));

  const validateMatrix = useCallback((): boolean => {
    const errs: string[] = [];
    if (zones.length === 0) errs.push(t("costservices:form.matrixAtLeastOneZone"));
    if (bands.length === 0) errs.push(t("costservices:form.matrixAtLeastOneBand"));

    zones.forEach((z, i) => {
      if (!z.name.trim())
        errs.push(t("costservices:form.zoneNameRequired", { index: i + 1 }));
      if (!z.postalFrom.trim())
        errs.push(
          t("costservices:form.zonePostalFromRequired", { index: i + 1 }),
        );
      if (!z.postalTo.trim())
        errs.push(t("costservices:form.zonePostalToRequired", { index: i + 1 }));
    });

    bands.forEach((b, i) => {
      if (b.minTn === "" || b.maxTn === "" || b.ldm === "") {
        errs.push(t("costservices:form.bandFieldsRequired", { index: i + 1 }));
      }
    });

    for (let z = 0; z < zones.length; z++) {
      for (let b = 0; b < bands.length; b++) {
        const val = parseFloat(prices[`${z}-${b}`] || "");
        if (isNaN(val) || val <= 0) {
          errs.push(
            t("costservices:form.priceMustBePositive", {
              zone:
                zones[z].name || `${t("costservices:form.zonePrefix")} ${z + 1}`,
              band:
                bands[b].label ||
                `${t("costservices:form.bandPrefix")} ${b + 1}`,
            }),
          );
        }
      }
    }

    setMatrixErrors(errs);
    return errs.length === 0;
  }, [zones, bands, prices, t]);

  const onFormSubmit = async (formData: FormValues) => {
    const resolvedPartnerId = selectedPartnerId;

    if (isMatrix) {
      if (!validateMatrix()) return;

      const builtZones: Omit<Zone, "_id">[] = zones.map((z) => ({
        name: z.name,
        postalFrom: z.postalFrom,
        postalTo: z.postalTo,
      }));
      const builtBands: Omit<Band, "_id">[] = bands.map((b) => ({
        label: b.label,
        minTn: parseFloat(String(b.minTn)),
        maxTn: parseFloat(String(b.maxTn)),
        ldm: parseFloat(String(b.ldm)),
      }));
      const builtMatrix: PriceMatrixIndexCell[] = [];
      for (let z = 0; z < zones.length; z++) {
        for (let b = 0; b < bands.length; b++) {
          builtMatrix.push({
            zoneIndex: z,
            bandIndex: b,
            price: parseFloat(prices[`${z}-${b}`]),
          });
        }
      }

      return onSubmit({
        name: formData.name,
        description: formData.description || "",
        type: "MATRIX",
        tax: formData.tax as any,
        partnerId: resolvedPartnerId,
        zones: builtZones as Zone[],
        bands: builtBands as Band[],
        priceMatrix: builtMatrix as any,
      });
    }

    return onSubmit({
      name: formData.name,
      description: formData.description || "",
      type: formData.type as any,
      price: formData.price ? parseFloat(formData.price) : 0,
      tax: formData.tax as any,
      partnerId: resolvedPartnerId,
    });
  };

  const SERVICE_TYPE_OPTIONS = [
    { value: "HOUR", label: "HOUR" },
    { value: "PIECE", label: "PIECE" },
    { value: "LIFT", label: "LIFT" },
    { value: "KM", label: "KM" },
    { value: "FLAT", label: "FLAT" },
    { value: "QTY", label: "QTY" },
    { value: "MATRIX", label: "Matrix" },
  ];

  const TAX_OPTIONS = [
    { value: "NO_TAX", label: "No Tax" },
    { value: "0", label: "0%" },
    { value: "24", label: "24%" },
  ];

  // ---------------------------------------------------------------------------
  // Read-only view (edit mode, before user clicks "Edit")
  // ---------------------------------------------------------------------------
  if (isEditMode && readonly) {
    const isMatrixView = service?.type === "MATRIX";

    return (
      <div className="mx-auto bg-gray-50 rounded-md overflow-hidden">
        <div className="p-3 space-y-2 overflow-y-auto">
          <SectionCard icon={Wrench} title={t("costservices:form.serviceCode")}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px]">
              {service?.partnerInfo && (
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="w-32 text-gray-500 text-[12px]">
                    {t("costservices:form.client")}
                  </dt>
                  <dd className="text-gray-900 flex items-center gap-2">
                    {service.partnerInfo.name}
                    {service.partnerInfo.isDeleted && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                        {t("costservices:form.deleted")}
                      </span>
                    )}
                  </dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("costservices:form.serviceCode")}
                </dt>
                <dd className="text-gray-900 font-medium" title={service?.name}>
                  {service?.name && service.name.length > 15
                    ? `${service.name.substring(0, 15)}...`
                    : service?.name}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("costservices:form.unitType")}
                </dt>
                <dd className="text-gray-900">
                  {service?.type || t("costservices:form.notSpecified")}
                </dd>
              </div>
              {!isMatrixView && (
                <div className="flex gap-2">
                  <dt className="w-32 text-gray-500 text-[12px]">
                    {t("costservices:form.price")}
                  </dt>
                  <dd className="text-gray-900">
                    {service?.price != null
                      ? `€${service.price.toFixed(2)}`
                      : t("costservices:form.notSpecified")}
                  </dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("costservices:form.tax")}
                </dt>
                <dd className="text-gray-900">
                  {service?.tax || t("costservices:form.noTax")}
                </dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("costservices:form.description")}
                </dt>
                <dd className="text-gray-900 whitespace-pre-wrap">
                  {service?.description || t("costservices:form.noDescription")}
                </dd>
              </div>
            </dl>
          </SectionCard>

          {isMatrixView && service?.zones && service?.bands && (
            <SectionCard
              icon={Grid3x3}
              title={t("costservices:form.matrixPricing")}
            >
              <PriceTable
                zones={service.zones.map((z) => ({
                  name: z.name,
                  postalFrom: z.postalFrom,
                  postalTo: z.postalTo,
                }))}
                bands={service.bands.map((b) => ({
                  label: b.label,
                  minTn: b.minTn,
                  maxTn: b.maxTn,
                  ldm: b.ldm,
                }))}
                prices={(() => {
                  const map: Record<string, string> = {};
                  service.priceMatrix?.forEach((cell) => {
                    const zIdx = service.zones!.findIndex(
                      (z) => z._id === cell.zoneId,
                    );
                    const bIdx = service.bands!.findIndex(
                      (b) => b._id === cell.bandId,
                    );
                    if (zIdx !== -1 && bIdx !== -1)
                      map[`${zIdx}-${bIdx}`] = String(cell.price);
                  });
                  return map;
                })()}
                onChange={() => {}}
                readonly
                weightLdmLabel={t("costservices:form.weightLdm")}
                zonePrefix={t("costservices:form.zonePrefix")}
                bandPrefix={t("costservices:form.bandPrefix")}
                allCellsNote={t("costservices:form.allCellsMustBeFilled")}
              />
            </SectionCard>
          )}
        </div>

        {!service?.partnerInfo?.isDeleted && (
          <div className="bg-white px-4 py-2.5 border-t border-gray-200 flex justify-end">
            <Button
              type="button"
              onClick={() => setReadonly(false)}
              variant="primary"
              icon={<Pencil className="w-3.5 h-3.5" />}
            >
              {t("common:buttons.edit")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Edit / Create form
  // ---------------------------------------------------------------------------
  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div className="p-3 space-y-2">
        <SectionCard icon={Wrench} title={t("costservices:form.serviceCode")}>
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("costservices:form.client")}
            >
              <PartnerSearchDropdown
                value={selectedPartnerId}
                onChange={handlePartnerChange}
                fetchPartners={fetchPartners}
                placeholder={t("costservices:form.selectGeneral")}
                disabled={readonly}
                initialPartner={initialPartner}
                showGeneralOption={true}
                generalOptionLabel={
                  t("costservices:page.generalNoPartner") ||
                  "General (no partner)"
                }
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("costservices:form.serviceCode")}
              required
              error={errors.name?.message}
            >
              <Input
                {...register("name")}
                size="sm"
                error={!!errors.name}
                placeholder={t("costservices:form.enterServiceCode")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("costservices:form.unitType")}
              required
              error={errors.type?.message}
            >
              <Select
                {...register("type")}
                size="sm"
                options={SERVICE_TYPE_OPTIONS}
                disabled={readonly}
                placeholder={t("costservices:form.selectType")}
                onChange={(e) => {
                  register("type").onChange(e);
                  onTypeChange?.(e.target.value);
                }}
              />
            </FormField>

            {!isMatrix && (
              <FormField
                layout="inline"
                size="sm"
                label={t("costservices:form.price")}
                required
                error={errors.price?.message}
              >
                <Input
                  {...register("price")}
                  type="number"
                  step="0.01"
                  size="sm"
                  placeholder="0.00"
                  error={!!errors.price}
                  readOnly={readonly}
                />
              </FormField>
            )}

            <FormField
              layout="inline"
              size="sm"
              label={t("costservices:form.tax")}
              required
              error={errors.tax?.message}
            >
              <Select
                {...register("tax")}
                size="sm"
                options={TAX_OPTIONS}
                disabled={readonly}
                placeholder={t("costservices:form.selectTax")}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("costservices:form.description")}
              error={errors.description?.message}
            >
              <Textarea
                {...register("description")}
                size="sm"
                error={!!errors.description}
                placeholder={t("costservices:form.enterDescription")}
                readOnly={readonly}
                rows={3}
              />
            </FormField>
          </div>
        </SectionCard>

        {isMatrix && (
          <SectionCard
            icon={Grid3x3}
            title={t("costservices:form.matrixPricing")}
          >
            <div className="space-y-3">
              {matrixErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                    {matrixErrors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Zones */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    {t("costservices:form.zones")}{" "}
                    <span className="text-gray-400 font-normal">
                      {t("costservices:form.zonesHint")}
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={prefillAllCounties}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      {t("costservices:form.prefillCounties")}
                    </button>
                    <button
                      type="button"
                      onClick={addZone}
                      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t("costservices:form.addZone")}
                    </button>
                  </div>
                </div>

                {zones.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    {t("costservices:form.noZonesYet")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {zones.map((zone, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-3 py-2"
                      >
                        <ZoneCombobox
                          value={zone.name}
                          onChange={(name) => updateZone(idx, "name", name)}
                          onSelect={(preset) => applyZonePreset(idx, preset)}
                          placeholder={t(
                            "costservices:form.zoneNamePlaceholder",
                          )}
                        />
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {t("costservices:form.from")}
                          </span>
                          <input
                            type="text"
                            value={zone.postalFrom}
                            onChange={(e) =>
                              updateZone(
                                idx,
                                "postalFrom",
                                e.target.value.slice(0, 6),
                              )
                            }
                            maxLength={6}
                            placeholder={t(
                              "costservices:form.postalFromPlaceholder",
                            )}
                            className="w-[72px] text-sm text-center border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-gray-400">–</span>
                          <input
                            type="text"
                            value={zone.postalTo}
                            onChange={(e) =>
                              updateZone(
                                idx,
                                "postalTo",
                                e.target.value.slice(0, 6),
                              )
                            }
                            maxLength={6}
                            placeholder={t(
                              "costservices:form.postalToPlaceholder",
                            )}
                            className="w-[72px] text-sm text-center border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeZone(idx)}
                          className="text-gray-400 hover:text-red-500 shrink-0 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bands */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    {t("costservices:form.weightBands")}{" "}
                    <span className="text-gray-400 font-normal">
                      {t("costservices:form.weightBandsHint")}
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      defaultValue={1}
                      id="cost-band-count-input"
                      className="w-14 text-sm border border-gray-300 rounded-md px-2 py-1.5 text-center"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.getElementById(
                          "cost-band-count-input",
                        ) as HTMLInputElement;
                        const count = parseInt(el?.value || "1", 10);
                        addBand(isNaN(count) || count < 1 ? 1 : count);
                      }}
                      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t("costservices:form.addBand")}
                    </button>
                  </div>
                </div>

                {bands.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    {t("costservices:form.noBandsYet")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {bands.map((band, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-3 py-2"
                      >
                        <Input
                          value={band.label}
                          onChange={(e) =>
                            updateBand(idx, "label", e.target.value)
                          }
                          placeholder={t(
                            "costservices:form.bandLabelPlaceholder",
                          )}
                          className="flex-1 min-w-0"
                          size="sm"
                        />
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            Min
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={String(band.minTn)}
                            onChange={(e) =>
                              updateBand(idx, "minTn", e.target.value)
                            }
                            placeholder="0"
                            className="w-[60px] text-sm text-center border border-gray-300 rounded-md px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            Max
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={String(band.maxTn)}
                            onChange={(e) =>
                              updateBand(idx, "maxTn", e.target.value)
                            }
                            placeholder="3"
                            className="w-[60px] text-sm text-center border border-gray-300 rounded-md px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {t("costservices:form.ldm")}
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={String(band.ldm)}
                            onChange={(e) =>
                              updateBand(idx, "ldm", e.target.value)
                            }
                            placeholder="1.6"
                            className="w-[60px] text-sm text-center border border-gray-300 rounded-md px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBand(idx)}
                          className="text-gray-400 hover:text-red-500 shrink-0 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {zones.length > 0 && bands.length > 0 && (
                <div className="w-full">
                  <p className="text-[12px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    {t("costservices:form.priceMatrix")}
                  </p>
                  <PriceTable
                    zones={zones}
                    bands={bands}
                    prices={prices}
                    onChange={handlePriceChange}
                    weightLdmLabel={t("costservices:form.weightLdm")}
                    zonePrefix={t("costservices:form.zonePrefix")}
                    bandPrefix={t("costservices:form.bandPrefix")}
                    allCellsNote={t("costservices:form.allCellsMustBeFilled")}
                  />
                </div>
              )}
            </div>
          </SectionCard>
        )}
      </div>

      <div className="flex justify-end items-center gap-3 px-4 py-2.5 border-t border-gray-200 bg-white shrink-0">
        {isEditMode && readonly && (
          <Button
            type="button"
            onClick={() => setReadonly(false)}
            variant="outline"
            icon={<Pencil className="w-3.5 h-3.5" />}
          >
            {t("common:buttons.edit")}
          </Button>
        )}
        {(!isEditMode || (isEditMode && !readonly)) && (
          <Button
            type="submit"
            isLoading={loading}
            disabled={loading}
            variant="primary"
          >
            {isEditMode
              ? t("common:buttons.saveChanges")
              : t("common:buttons.create")}
          </Button>
        )}
      </div>
    </form>
  );
};
